import { useUserData } from '@conteg/auth';
import {
  Alert,
  Button,
  Flex,
  Heading,
  Option,
  showAlertModalError,
  styled,
} from '@conteg/ui';
import { FormCheckbox } from 'components/inputs/form-checkbox';
import Notification from 'components/notification/notification';
import { Select } from 'components/select/select';
import { CUSTOM_AUTH_TOKEN } from 'config';
import i18n from 'i18next';
import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ServiceType,
  StockOutReservationDtoInput,
  StorageUnitDocument,
  usePickupReservationMutation,
  useStockOutReservationMutation,
} from 'types/generated/graphql';
import { usePoint } from 'utils/device/device-store';
import { resolveTwoFactorErrorKey } from 'utils/errors/resolve-two-factor-error-key';
import { useInvalidateQueries } from 'utils/hooks/use-ivalidate-queries';

type FormInputs = {
  state: 'PICK_UP' | 'STOCK_OUT';
  isPickedUpBySender: boolean;
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5rem;
  margin-top: 5rem;
`;

const getOptions = (
  serviceType: ServiceType | null | undefined,
  isDeliverySubmission: boolean | undefined
) => {
  let options: Option[] = [
    {
      label: i18n.t(
        'Page.StorageUnit.Actions.UpdateReservationState.Select.Option.Handover'
      ),
      value: 'PICK_UP',
    },
  ];

  if (serviceType === ServiceType.Delivery) {
    options.push({
      label: i18n.t(
        'Page.StorageUnit.Actions.UpdateReservationState.Select.Option.StockOut'
      ),
      value: 'STOCK_OUT',
    });
  }

  if (isDeliverySubmission) {
    options = options.filter((option) => option.value !== 'PICK_UP');
  }

  return options;
};

type UpdateReservationStateProps = {
  reservationId: string;
  tenantSubjectId: string;
  customerPinCredentialId?: string;
  trackingIdentifier?: string | null | undefined;
  serviceType?: ServiceType | null | undefined;
  isDeliverySubmission: boolean | undefined;
  storageUnitId: string;
  /** The parcel is still waiting for the recipient's release code (UPS EPRA). */
  isTwoFactorPending?: boolean;
};

const UpdateReservationState = ({
  reservationId,
  trackingIdentifier,
  tenantSubjectId,
  customerPinCredentialId,
  serviceType,
  isDeliverySubmission,
  isTwoFactorPending,
}: UpdateReservationStateProps) => {
  const { t } = useTranslation();
  const { pointId } = usePoint();
  const userData = useUserData(CUSTOM_AUTH_TOKEN);
  const userId = userData?.userId;

  const invalidate = useInvalidateQueries();

  const [formNotice, setFormNotice] = useState<string>('');

  const { mutate: pickupReservation, isPending: isPickupPending } =
    usePickupReservationMutation();
  const { mutate: stockOutReservation, isPending: isStockOutPending } =
    useStockOutReservationMutation();

  const formActions = useForm<FormInputs>({
    defaultValues: {
      state: undefined,
    },
  });

  const stateWatch = formActions.watch('state');

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = formActions;

  const serviceTypeToEnum = (
    value: ServiceType | null | undefined
  ): ServiceType | undefined => {
    if (!value) return undefined;
    switch (value) {
      case ServiceType.Delivery:
        return ServiceType.Delivery;
      case ServiceType.HandToHand:
        return ServiceType.HandToHand;
      case ServiceType.BagToBox:
        return ServiceType.BagToBox;
      default:
        return undefined;
    }
  };

  const handleUpdateReservationState: SubmitHandler<FormInputs> = ({
    state,
    isPickedUpBySender: _isPickedUpBySender,
  }) => {
    setFormNotice('');
    const onSuccess = () => {
      setFormNotice('Page.StorageUnit.Actions.UpdateReservationState.Success');
      invalidate([StorageUnitDocument]);
      reset();
    };

    if (state === 'PICK_UP') {
      pickupReservation(
        {
          input: {
            reservationId,
            pointId: pointId as string,
            tenantSubjectId,
            customerPinCredentialId,
            userId,
          },
        },
        {
          onSuccess,
          onError: (error) =>
            showAlertModalError(
              t(
                resolveTwoFactorErrorKey(
                  error,
                  'Page.StorageUnit.Actions.UpdateReservationState.Error'
                )
              ),
              error
            ),
        }
      );
      return;
    }

    const stockOutInput: StockOutReservationDtoInput = {
      reservationId,
      pointId: pointId as string,
      tenantSubjectId,
      trackingIdentifier,
      userId,
      serviceType: serviceTypeToEnum(serviceType),
    };

    stockOutReservation(
      {
        input: stockOutInput,
      },
      {
        onSuccess,
        onError: (error) =>
          showAlertModalError(
            t(
              resolveTwoFactorErrorKey(
                error,
                'Page.StorageUnit.Actions.UpdateReservationState.Error'
              )
            ),
            error
          ),
      }
    );
  };

  return (
    <FormProvider {...formActions}>
      <Form onSubmit={handleSubmit(handleUpdateReservationState)}>
        <Heading
          variant="h2"
          title={t('Page.StorageUnit.Actions.UpdateReservationState.Heading')}
        />
        {formNotice && (
          <Notification
            testId="update-reservation-state-notification"
            message={formNotice}
          />
        )}
        <Notification
          testId="update-reservation-state-notification-warning"
          message="Page.StorageUnit.Actions.UpdateReservationState.Warning"
        />
        <Select
          name="state"
          options={getOptions(serviceType, isDeliverySubmission)}
          label={t(
            'Page.StorageUnit.Actions.UpdateReservationState.Select.Text'
          )}
          isNullable={false}
          required
          error={errors?.state || undefined}
        />
        {isTwoFactorPending && stateWatch == 'PICK_UP' && (
          // Reads as a continuation of the two-factor warning at the top of the modal, which is shown
          // under exactly the same condition (isTwoFactorPending) and states the main point - that the
          // parcel is being released without the code. This one only adds what is specific to recording
          // a customer pickup, so it stays short and does not repeat the POZOR.
          //
          // It cannot live in that shared warning: the warning renders before the operator picks a state,
          // and marking a stock-out does inform the carrier (it sends a dispossession scan), so the
          // sentence would be false for the other option.
          <Flex mt="4rem" width="100%">
            <Alert
              testId="two-factor-pickup-carrier-warning"
              type="warning"
              message={t(
                'Page.StorageUnit.Actions.UpdateReservationState.TwoFactor.CarrierNotInformed'
              )}
            />
          </Flex>
        )}
        {serviceType == ServiceType.HandToHand && stateWatch == 'PICK_UP' && (
          <FormCheckbox
            name="isPickedUpBySender"
            testId="is-picked-up-by-sender-checkbox"
            label={t(
              'Page.StorageUnit.Actions.UpdateReservationState.IsPickedUpBySender'
            )}
          />
        )}
        <Button
          title={t('Page.StorageUnit.Actions.UpdateReservationState.Button')}
          testId="open-without-pin-button"
          variant="primary"
          size="xl"
          disabled={isPickupPending || isStockOutPending}
        />
      </Form>
    </FormProvider>
  );
};

export default UpdateReservationState;
